import { filterProvidersByDept } from '../../visit/departmentResolution';

/**
 * assigneeCandidates — pure selector for the add-procedure "Assign to" list.
 *
 * A2-S6 follow-up. Composes the TWO gates the picker must satisfy, in one
 * place so neither can drift from the reassign picker / registration:
 *
 *   1. ROLE eligibility — only EXECUTE_SITTING-capable roles (DOCTOR / NURSE)
 *      can own a sitting. (unchanged existing rule)
 *   2. DEPARTMENT scope — role-eligible staff ∩ (staff in the VISIT'S stamped
 *      department OR staff with NO department). Staff in OTHER departments are
 *      excluded. NO-department staff are ORG-WIDE and MUST stay visible in
 *      every department's picker (the empty-set carve-out). Delegated to the
 *      shared `filterProvidersByDept` util (single source of truth) which also
 *      fail-opens on un-hydrated (undefined) edges — this is a display filter;
 *      the server re-validates on submit.
 *
 * Degrade gracefully: when `visitDepartmentId` is falsy (org with 0 depts, or
 * a visit with no/emergency department), NO dept filter is applied — the full
 * role-eligible list shows, consistent with A2 elsewhere.
 *
 * The viewer (self) is always excluded here because the picker renders a
 * dedicated "self" avatar; self-assign is handled outside the candidate list.
 *
 * @param {Array}  staff              org staff rows — [{ member_id, role, ... }]
 * @param {object} args
 * @param {string} [args.viewerMemberId]     the logged-in member — excluded (rendered as "self")
 * @param {string} [args.visitDepartmentId]  the visit's stamped department_id ('' | null → no filter)
 * @param {object} [args.deptsByMember]      member_id → [{ dept_id }] map (departmentStore)
 * @returns {Array} the filtered candidate rows
 */
export const ELIGIBLE_ROLES = new Set(['DOCTOR', 'NURSE']);

export function selectAssigneeCandidates(staff, {
  viewerMemberId,
  visitDepartmentId,
  deptsByMember,
} = {}) {
  const roleEligible = (staff || [])
    .filter((m) => ELIGIBLE_ROLES.has((m.role || '').toUpperCase()))
    .filter((m) => m.member_id !== viewerMemberId);
  // Single source: same reverse dept filter registration + reassign use. Keeps
  // no-department (org-wide) staff and un-hydrated edges (fail-open).
  return filterProvidersByDept(roleEligible, visitDepartmentId, deptsByMember);
}
